import { useQuery } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import AppreciateSvg from '../assets/icons/AppreciateSvg';
import { Button } from '../components/_atoms/Button';
import { IconWrapper } from '../components/_atoms/IconWrapper';
import MainLoader from '../components/Loader';
import { useMetrics } from '../hooks/useMetrics';
import useRouterMatch from '../hooks/useRouterMatch';
import useSignIn from '../hooks/useSignIn';
import nextI18nextConfig from '../next-i18next.config';
import backendService from '../services/backendService';

const AuthenticatedCopyGameTemplate = () => {
  const router = useRouter();
  const { t } = useTranslation('CopyGameTemplatePage');
  const { tenantId, templateId, gameId } = useRouterMatch();
  const metrics = useMetrics();

  const { data, isFetching } = useQuery(
    ['copyTemplate', templateId, tenantId, gameId],
    () => {
      if (templateId && tenantId) {
        metrics.logEvent('Template.Use', { templateId, tenantId });
        return backendService.instance.copyTemplate(templateId, tenantId);
      } else if (tenantId && gameId) {
        metrics.logEvent('Template.Game.Use', { gameId, tenantId });
        return backendService.instance.copyAsTemplate(gameId, tenantId);
      } else {
        return Promise.resolve(undefined);
      }
    },
    {
      enabled: Boolean(templateId),
    },
  );

  useEffect(() => {
    if (data?.template && data?.tenantId) {
      router.push(`/${data?.tenantId}/lessons/${data?.template.id}/edit`);
    }
  }, [data, router]);
  return (
    <>
      {isFetching && (
        <MainLoader>
          <Title>{t('Creating a lesson based on the awesome template')}...</Title>
        </MainLoader>
      )}
    </>
  );
};

const UnAuthenticatedCopyGameTemplate = () => {
  const router = useRouter();
  const { signIn } = useSignIn();

  const { t } = useTranslation('CopyGameTemplatePage');

  const handleLogin = () => {
    signIn({ callbackUrl: '/' });
  };

  const handleGoToSignup = () => {
    router.push(`/signup?callback=${window.location.href}`, undefined);
  };
  return (
    <Container>
      <IconWrapper height="100px" width="100px" style={{ marginBottom: 20 }}>
        <AppreciateSvg />
      </IconWrapper>
      <div>
        <Title>
          {t('In order to use this awesome template, please create an account for free')}
        </Title>
      </div>
      <Button styles={{ marginBottom: 40 }} type="primary" onClick={handleGoToSignup}>
        {t('Sign up now')}
      </Button>
      <LoginP>
        {t('Or')}{' '}
        <LoginText role="button" onClick={handleLogin}>
          {t('login with your existing account')}
        </LoginText>
      </LoginP>
    </Container>
  );
};

const Title = styled.h3`
  font-weight: bold;
  font-size: 26px;
  width: 100%;
  text-align: center;
  @media (max-width: 900px) {
    font-size: 20px;
  }
`;

const LoginText = styled.span`
  text-decoration: underline;
  font-weight: bold;
  font-size: 18px;
  @media (max-width: 900px) {
    font-size: 14px;
  }
  cursor: pointer;
`;

const LoginP = styled.p`
  font-weight: bold;
  font-size: 18px;
  @media (max-width: 900px) {
    font-size: 14px;
  }
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 20px;
  & > * {
    max-width: 800px;
  }
`;

const FromTemplatePage = () => {
  const session = useSession();
  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {session.status === 'authenticated' && <AuthenticatedCopyGameTemplate />}
      {session.status === 'unauthenticated' && <UnAuthenticatedCopyGameTemplate />}
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  const localisation = context.req.cookies.i18nLang
    ? await serverSideTranslations(
        context.req.cookies.i18nLang,
        ['CopyGameTemplatePage'],
        nextI18nextConfig,
      )
    : {};

  return {
    props: {
      ...localisation,
    },
  };
};

export default FromTemplatePage;
